import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProgressService } from '../../store/slices/progressServiceDashboardSlice';

import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

import {
	tableToLayerName,
	productLayerOrder,
	productHidden,
} from '../../config/layerDictionary';
import useBlockMapScroll from '../../hooks/useBlockMapScroll';

import './ProgressDashboardService.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const normalizePercent = value => {
	const percent = Number(value);

	if (!Number.isFinite(percent)) return 0;

	return Math.min(Math.max(percent, 0), 100);
};

const getDisplayedPercent = value => {
	const percent = normalizePercent(value);

	return percent >= 100 ? 100 : Math.min(Math.round(percent), 99);
};

export default function ProgressDashboardService() {
	const dispatch = useDispatch();
	const [alreadyFetched, setAlreadyFetched] = useState(false);
    const [isOpen, setIsOpen] = useState(window.innerWidth > 768);
	
	const scrollRef = useRef(null);
	useBlockMapScroll(scrollRef);

	useEffect(() => {
		if (!alreadyFetched) {
			dispatch(fetchProgressService());
			setAlreadyFetched(true);
		}
	}, [dispatch, alreadyFetched]);

	const { data, status, error } = useSelector(state => state.progressService);

	const centerTextPluginTotal = {
		id: 'centerTextTotal',
		beforeDraw: chart => {
			const { ctx, width, height } = chart;
			const dataset = chart.config.data.datasets[0].data;
			const executed = dataset[0];
			const total = dataset.reduce((a, b) => a + b, 0);
			const percent = getDisplayedPercent(
				total > 0 ? (executed / total) * 100 : 0
			);
			ctx.save();
			ctx.font = 'bold 12px Arial';
			ctx.fillStyle = '#000';
			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';
			ctx.fillText(`${percent}%`, width / 2, height / 2);
			ctx.restore();
		},
	};

	const dataFiltered = (Array.isArray(data) ? [...data] : [])
		.filter(item => {
			const nome =
				tableToLayerName[item.nomeTabelaCalculada] ||
				item.nomeTabelaCalculada;
			return !productHidden.includes(nome);
		})
		.sort((a, b) => {
			const nomeA =
				tableToLayerName[a.nomeTabelaCalculada] ||
				a.nomeTabelaCalculada;
			const nomeB =
				tableToLayerName[b.nomeTabelaCalculada] ||
				b.nomeTabelaCalculada;
			return (
				productLayerOrder.indexOf(nomeA) -
				productLayerOrder.indexOf(nomeB)
			);
		});

	const percents = dataFiltered.map(x => x.porcentagemExecutada || 0);
	const overall = Math.round(
		percents.reduce((acc, v) => acc + v, 0) / Math.max(percents.length, 1)
	);

	const PCT_FIXA_APRESENTAÇÃO = 71;
	const totalChartData = {
		labels: ['Executado', 'À executar'],
		datasets: [
			{
				data:  [PCT_FIXA_APRESENTAÇÃO, 100 - PCT_FIXA_APRESENTAÇÃO], //[overall, 100 - overall],
				backgroundColor: ['#111755', '#d9d9d9'],
				borderWidth: 1,
			},
		],
	};

	const chartOptionsTotal = {
		cutout: '70%',
		plugins: { legend: { display: false }, tooltip: { enabled: false } },
		maintainAspectRatio: false,
	};

	return (
		<div>
			<div
				className={`right-panel-wrapper ${isOpen ? 'open' : 'closed'}`}
			>
				<div className="right-panel">
					{status === 'loading' && <p>Carregando dados...</p>}
					{status === 'failed' && <p>Erro: {error}</p>}

					{status === 'succeeded' && Array.isArray(data) && (
						<div
							className="table-scroll"
							ref={scrollRef}
							onWheelCapture={e => {
								e.stopPropagation();
							}}
							onTouchStartCapture={e => {
								e.stopPropagation();
							}}
							onTouchMoveCapture={e => {
								e.stopPropagation();
							}}
						>
							<table className="progress-table">
								<thead>
									<tr className="thead-summary">
										<th className="product-col">
											<div className="summary-title">
												Andamento do Projeto
											</div>
										</th>
										<th className="chart-col">
											<div className="donut-wrapper">
												<Doughnut
													data={totalChartData}
													options={chartOptionsTotal}
													plugins={[
														centerTextPluginTotal,
													]}
												/>
											</div>
										</th>
									</tr>
									<tr>
										<th>Produtos</th>
										<th>% Realizado</th>
									</tr>
								</thead>
								<tbody>
									{[...data]
										.filter(item => {
											const nome =
												tableToLayerName[
													item.nomeTabelaCalculada
												] || item.nomeTabelaCalculada;
											return !productHidden.includes(
												nome
											);
										})
										.sort((a, b) => {
											const nomeA =
												tableToLayerName[
													a.nomeTabelaCalculada
												] || a.nomeTabelaCalculada;
											const nomeB =
												tableToLayerName[
													b.nomeTabelaCalculada
												] || b.nomeTabelaCalculada;
											return (
												productLayerOrder.indexOf(
													nomeA
												) -
												productLayerOrder.indexOf(nomeB)
											);
										})
										.map((item, idx) => {
											const percent =
												item.porcentagemExecutada || 0;
											const chartData = {
												labels: [
													'Executado',
													'À executar',
												],
												datasets: [
													{
														data: [
															percent,
															100 - percent,
														],
														backgroundColor: [
															'#111755',
															'#d9d9d9',
														],
														borderWidth: 1,
													},
												],
											};
											const chartOptions = {
												cutout: '70%',
												plugins: {
													legend: { display: false },
													tooltip: { enabled: false },
												},
												maintainAspectRatio: false,
											};
											return (
												<tr key={idx}>
													<td
														className="product-cell"
														title={
															tableToLayerName[
																item
																	.nomeTabelaCalculada
															] ||
															item.nomeTabelaCalculada
														}
													>
														{tableToLayerName[
															item
																.nomeTabelaCalculada
														] ||
															item.nomeTabelaCalculada}
													</td>
													<td className="chart-cell">
														<div className="donut-wrapper">
															<Doughnut
																data={chartData}
																options={
																	chartOptions
																}
																plugins={[
																	centerTextPluginTotal,
																]}
															/>
														</div>
													</td>
												</tr>
											);
										})}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
			<div
				className="right-panel-toggle"
				onClick={() => setIsOpen(!isOpen)}
			>
				{isOpen ? '▶' : '◀'}
			</div>
		</div>
	);
}
